/* eslint-disable arrow-body-style */
/* eslint-disable newline-before-return */
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import {
  FieldImpact,
  FormField,
  FormFieldValue,
  FormOption,
} from "@/components/FormEngine/types";

import { BaseRendererProps } from "../../types";

/* =========================
   Helpers de normalisation
   ========================= */

const getFieldState = (
  formState: Record<string, FormFieldValue>,
  field: FormField,
): FormFieldValue => formState[field.id] ?? field.value;

const buildOptionValue = (
  options: FormOption[] | undefined,
  optionId: string | number,
): FormFieldValue => {
  const opt = options?.find((o) => o.id === String(optionId));
  return {
    id: String(optionId),
    value: opt ? opt.id : String(optionId),
    text: opt?.value,
    impacts: opt?.impacts as FieldImpact[] | undefined,
  };
};

const patchValue = (
  prev: FormFieldValue | undefined,
  nextPrimitive: unknown,
): FormFieldValue => ({
  id: prev?.id ?? "",
  text: prev?.text,
  impacts: prev?.impacts,
  value: nextPrimitive as any,
});

/* =========================
   Rendu d'un champ
   ========================= */

const FieldDispatcher: React.FC<{
  field: FormField;
  value: FormFieldValue;
  onChange: (fieldId: string, next: FormFieldValue) => void;
}> = ({ field, value, onChange }) => {
  const writable = field.writable;
  const required = field.required;

  const handleTextChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { value: unknown } },
  ) => {
    onChange(field.id, patchValue(value, event.target.value));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field.id, patchValue(value, event.target.checked));
  };

  switch (field.type) {
    case "text":
    case "date":
      return (
        <TextField
          label={field.name}
          value={(value?.value ?? "") as any}
          onChange={handleTextChange}
          disabled={!writable}
          required={required}
          fullWidth
          variant='outlined'
        />
      );

    case "select":
    case "radiobox": {
      const options = field.options ?? [];
      const selectValue =
        value?.id && options.some((o) => o.id === value.id) ? value.id : "";
      const handleSelectChange = (
        event:
          | React.ChangeEvent<{ value: unknown }>
          | { target: { value: unknown } },
      ) => {
        const nextId = String(event.target.value ?? "");
        onChange(field.id, buildOptionValue(options, nextId));
      };

      return (
        <FormControl fullWidth disabled={!writable} required={required}>
          <FormLabel>{field.name}</FormLabel>
          <Select
            value={selectValue}
            onChange={handleSelectChange as any}
            displayEmpty
          >
            <MenuItem value=''>
              <em>—</em>
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.value || option.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    case "checkbox":
      return (
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value?.value)}
                onChange={handleCheckboxChange}
                disabled={!writable}
              />
            }
            label={field.name}
          />
        </FormGroup>
      );

    default:
      return (
        <TextField
          label={`${field.name} (type: ${field.type})`}
          value={(value?.value ?? "") as any}
          onChange={handleTextChange}
          disabled
          fullWidth
          variant='filled'
          helperText='Type de champ non supporté pour le rendu interactif.'
        />
      );
  }
};

interface FieldRendererProps extends BaseRendererProps {
  field: FormField;
  /** Si true, masque; si false/undefined, affiche (RowRenderer filtre déjà). */
  effectiveHidden?: boolean;
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  formState,
  onFieldChange,
  effectiveHidden,
}) => {
  if (effectiveHidden === true) return null; // cohérent avec filtrage amont
  return (
    <Box>
      <Typography
        variant='caption'
        color='text.secondary'
        sx={{ mb: 1, display: "block" }}
      >
        Ligne {field.row}, Colonne {field.col}
      </Typography>

      <FieldDispatcher
        field={field}
        value={getFieldState(formState, field)}
        onChange={onFieldChange}
      />
    </Box>
  );
};
